import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Ruler, MapPin, Check, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { getConfig } from "@/lib/config";
import Seo from "@/components/Seo";
import PropertyCard from "@/components/PropertyCard";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";
import { formatPrice, formatNumber, PURPOSE_LABELS, whatsappLink } from "@/lib/format";
import { FALLBACK_IMG } from "@/lib/images";

export default function PropertyDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [wa, setWa] = useState("966570452861");
  const [active, setActive] = useState(0);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    setActive(0);
    api
      .get(`/properties/${id}`)
      .then((r) => {
        setItem(r.data.item);
        setSimilar(r.data.similar);
        setState("ok");
      })
      .catch(() => setState("notfound"));
    getConfig().then((c) => setWa(c.whatsapp)).catch(() => {});
  }, [id]);

  if (state === "loading") {
    return (
      <div className="container-lux py-16 space-y-6" data-testid="property-loading">
        <div className="h-[420px] animate-pulse rounded-2xl bg-forest/5" />
        <div className="h-8 w-1/3 animate-pulse rounded bg-forest/5" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-forest/5" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="container-lux py-32 flex flex-col items-center gap-4 text-center" data-testid="property-not-found">
        <h1 className="font-heading font-bold text-2xl text-forest">هذا العقار غير موجود</h1>
        <Link to="/properties" className="btn-gold text-sm" data-testid="back-to-listings">
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
          العودة للعقارات
        </Link>
      </div>
    );
  }

  const images = item.images?.length ? item.images : [FALLBACK_IMG];

  return (
    <>
      <Seo title={item.title} description={`${item.title} — ${item.city}. عقار افتراضي لأغراض العرض التجريبي.`} />
      <section className="container-lux py-10 md:py-14" data-testid="property-detail">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-4">
          <div className="relative h-[320px] md:h-[480px] overflow-hidden rounded-3xl">
            <img src={images[active]} alt={`${item.title} — صورة ${active + 1}`} className="h-full w-full object-cover" data-testid="property-main-image" />
            <span className={`absolute top-5 start-5 rounded-full px-5 py-2 text-sm font-heading font-bold ${item.purpose === "sale" ? "bg-forest text-ivory" : "bg-gold text-forest"}`} data-testid="property-purpose-badge">
              {PURPOSE_LABELS[item.purpose]}
            </span>
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="property-thumbnails">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`عرض الصورة ${i + 1}`}
                  data-testid={`property-thumb-${i}`}
                  className={`h-20 w-28 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${i === active ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
          <div className="lg:col-span-8 space-y-10">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-forest/5 px-4 py-1.5 text-xs font-heading font-semibold text-forest" data-testid="property-type-badge">{item.type}</span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="property-location">
                  <MapPin className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                  {item.city}{item.district ? ` — ${item.district}` : ""}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-forest tracking-tight">{item.title}</h1>
              <p className="font-heading font-extrabold text-2xl md:text-3xl text-gold-dark" data-testid="property-price">{formatPrice(item.price, item.purpose)}</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="property-specs">
              {item.bedrooms > 0 && (
                <div className="rounded-2xl bg-white p-5 text-center shadow-[0_10px_40px_rgba(10,42,26,0.05)]">
                  <BedDouble className="h-6 w-6 text-gold-dark mx-auto mb-2" aria-hidden="true" />
                  <p className="font-heading font-bold text-forest">{formatNumber(item.bedrooms)}</p>
                  <p className="text-xs text-muted-foreground">غرف نوم</p>
                </div>
              )}
              {item.bathrooms > 0 && (
                <div className="rounded-2xl bg-white p-5 text-center shadow-[0_10px_40px_rgba(10,42,26,0.05)]">
                  <Bath className="h-6 w-6 text-gold-dark mx-auto mb-2" aria-hidden="true" />
                  <p className="font-heading font-bold text-forest">{formatNumber(item.bathrooms)}</p>
                  <p className="text-xs text-muted-foreground">دورات مياه</p>
                </div>
              )}
              <div className="rounded-2xl bg-white p-5 text-center shadow-[0_10px_40px_rgba(10,42,26,0.05)]">
                <Ruler className="h-6 w-6 text-gold-dark mx-auto mb-2" aria-hidden="true" />
                <p className="font-heading font-bold text-forest">{formatNumber(item.area)} م²</p>
                <p className="text-xs text-muted-foreground">المساحة</p>
              </div>
              <div className="rounded-2xl bg-white p-5 text-center shadow-[0_10px_40px_rgba(10,42,26,0.05)]">
                <MapPin className="h-6 w-6 text-gold-dark mx-auto mb-2" aria-hidden="true" />
                <p className="font-heading font-bold text-forest">{item.city}</p>
                <p className="text-xs text-muted-foreground">المدينة</p>
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="font-heading font-bold text-2xl text-forest">عن العقار</h2>
              <p className="leading-relaxed text-charcoal/80" data-testid="property-description">{item.description}</p>
            </div>

            {item.amenities?.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-2xl text-forest">المميزات والمرافق</h2>
                <ul className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="property-amenities">
                  {item.amenities.map((a) => (
                    <li key={a} className="flex items-center gap-2 rounded-xl bg-white px-4 py-3 text-sm shadow-[0_10px_40px_rgba(10,42,26,0.04)]">
                      <Check className="h-4 w-4 text-gold-dark shrink-0" aria-hidden="true" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-5">
              <div className="rounded-2xl bg-forest p-7 text-ivory space-y-5" data-testid="property-contact-card">
                <h3 className="font-heading font-bold text-xl">مهتم بهذا العقار؟</h3>
                <p className="text-sm text-ivory/70 leading-relaxed">تواصل مع فريق الوساطة للاستفسار أو لحجز موعد معاينة.</p>
                <a
                  href={whatsappLink(wa, `مرحباً، أرغب بالاستفسار عن: ${item.title} (${item.city}) — استفسار تجريبي`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-heading font-bold text-white transition-transform hover:-translate-y-0.5"
                  data-testid="property-whatsapp-button"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                  تواصل عبر واتساب
                </a>
                <Link to="/request" className="flex items-center justify-center gap-2 rounded-full border border-ivory/25 px-6 py-3 font-heading font-semibold text-sm text-ivory transition-colors hover:bg-ivory/10" data-testid="property-request-button">
                  اطلب عقاراً مشابهاً
                </Link>
                <p className="text-[11px] text-gold/80 leading-relaxed border-t border-ivory/10 pt-4">
                  بيانات التواصل في هذا النموذج تجريبية — هذا العقار افتراضي وليس متاحاً فعلياً.
                </p>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-4 text-xs text-charcoal/80 leading-relaxed" data-testid="property-demo-note">
                <AlertTriangle className="h-4 w-4 text-gold-dark shrink-0 mt-0.5" aria-hidden="true" />
                نموذج تجريبي: الصور والسعر والمواصفات المعروضة افتراضية لأغراض العرض التقديمي فقط.
              </div>
            </div>
          </aside>
        </div>

        {similar.length > 0 && (
          <div className="mt-20 space-y-8" data-testid="similar-listings">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-forest">عقارات مشابهة</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {similar.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
